import { isEmpty } from 'lodash'

import constants from 'app/constants'
import { sentryLogger } from 'sentry'

import {
  apiEndpoints,
  validateDeleteRequest,
  validateUpdateRequest
} from './common'

import { makeRequestUrl, showToast } from '../app/helpers'
import { get, patch, post, remove } from '../request'

const batchRequestStructure = (data) => ({
  teacher: data.teacher,
  // Equal teacher set (Batch.teachers M2M). Normalise to UUIDs whether the form
  // gives us already-selected ids (touched) or seeded teacher objects (untouched).
  // Omitted entirely when absent, so non-multi-teacher flows are unaffected.
  ...(Array.isArray(data.teachers)
    ? {
        teachers: data.teachers.map((t) =>
          typeof t === 'string' ? t : t?.user?.uuid || t?.uuid
        )
      }
    : {}),
  course: data.course,
  fee: data.fee,
  coins_per_session: data.coins_per_session,
  coins_per_month: data.coins_per_month,
  batch_type: data.type,
  centre: data.centre,
  enrolment_capacity: data.enrolment_capacity,
  publish: data.publish,
  reason: data.reason,
  single_session_capacity: data.single_session_capacity,
  batch_activities: data?.batch_activities,
  // Timings/cadence is only sent when the Super-Admin timings editor actually
  // changed it (BatchTimingsWidget sets `cadence_changed`). Ordinary batch
  // edits omit it entirely, so they never trip the backend's cadence-resync
  // signal that regenerates future schedules + attendance.
  ...(data.cadence_changed && Array.isArray(data.cadence)
    ? { cadence: data.cadence }
    : {})
})

const fetchBatch = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.fetchBatch, queries)
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    // showToast('Oops, Error in fetching batch', constants.ERROR)

    sentryLogger(error, 'error in fetching batch')
    throw error
  }
}

const showRecordings = async (data) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.oh_schedules}/dashboard`, {
        batch_uuid: data.uuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in show recording')
    throw error
  }
}

const createBatch = async (data) => {
  try {
    const response = await post(apiEndpoints.batch, batchRequestStructure(data))
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating batch')
    throw error
  }
}

const updateBatch = async (data) => {
  validateUpdateRequest(data)
  try {
    const response = await patch(
      `${apiEndpoints.batch}/${data.uuid}/`,
      batchRequestStructure(data)
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating batch')
    throw error
  }
}

const updateBatchIsRecordable = async (data, is_recordable) => {
  try {
    const response = await patch(`${apiEndpoints.batch}/${data.uuid}/`, {
      is_recordable
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating recording')
    throw error
  }
}

const deleteBatch = async (value) => {
  const { ids, queries } = value
  validateDeleteRequest(ids)

  try {
    if (ids.length === 1) {
      await remove(`${apiEndpoints.batch}/${ids[0]}/`, {}, queries)

      return ids
    } else {
      throw new Error('Only one batch can be deleted at a time')
    }
  } catch (error) {
    sentryLogger(error, 'error in deleting batch')
    throw error
  }
}

const getBatchData = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.batch, queries)
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching batch')
    throw error
  }
}

const createSummerCampBatches = async ({ file, rows, dryRun = false } = {}) => {
  try {
    let payload
    let options
    if (file) {
      const formData = new FormData()
      formData.append('file', file, file.name)
      formData.append('dry_run', dryRun ? 'true' : 'false')
      payload = formData
      options = { headers: { 'Content-Type': 'multipart/form-data' } }
    } else {
      payload = { rows: rows || [], dry_run: !!dryRun }
      options = {}
    }
    const response = await post(
      `${apiEndpoints.summerCampBatch}/`,
      payload,
      options
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating summer camp batches')
    throw error
  }
}

// Options for the Super-Admin preschool batch form: centres, programmes with
// the grades they actually have a Course for, and academic years. Pass a centre
// uuid to also get that centre's rooms with their current batch counts.
const fetchPreschoolBatchOptions = async ({ centre } = {}) => {
  try {
    const response = await get(
      makeRequestUrl(
        `${apiEndpoints.preschoolBatchOptions}/`,
        centre ? { centre } : {}
      )
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching preschool batch options')
    throw error
  }
}

// Creates one preschool batch, its year of schedules and its capacity row in a
// single transaction. Super-Admin only, enforced on the endpoint as well as here.
const createPreschoolBatch = async (data) => {
  try {
    const response = await post(`${apiEndpoints.preschoolBatch}/`, data)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating preschool batch')
    throw error
  }
}

export {
  fetchBatch,
  showRecordings,
  createBatch,
  updateBatch,
  updateBatchIsRecordable,
  deleteBatch,
  getBatchData,
  createSummerCampBatches,
  fetchPreschoolBatchOptions,
  createPreschoolBatch
}
